import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Title from '../../../components/Title';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Divider, Row } from 'antd';
import CustomInput from '../../../components/CustomInput';
import CustomSelect from '../../../components/CustomSelect';
import MultinationalTabs from '../../../components/MultinationalTabs';
import ContactEditors from './ContactEditors';
import CustomMultiselect from '../../../components/CustomMultiselect';
import { createShallowCopy } from '../../../utils/helpFunctions';
import { allClients, getClients } from '../../../redux/slices/clientsSlice';
import {
    createContact,
    editContact,
    getContactById,
    oneContact,
    resetContact,
} from '../../../redux/slices/contactSlice';
import { toast } from 'react-toastify';
import LocalLoader from '../../../components/LocalLoader';

export default function CreateContactPage() {
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    const clients = useSelector(allClients);
    const contact = useSelector(oneContact);

    const countriesLocalizationsData = useMemo(
        () =>
            contact?.localizations?.reduce((acc, el) => {
                acc[el.lang_code.toLowerCase()] = el;
                return acc;
            }, {}),
        [contact?.localizations],
    );

    const selectPartnersOptions = useMemo(
        () =>
            clients?.map((el) => ({
                id: el.name,
                value: el.name,
                label: el.name,
            })),
        [clients],
    );

    const selectProvidersOptions = useMemo(
        () => [
            { id: 'mysafety', value: 'mysafety', label: 'MySafety' },
            { id: 'tryg', value: 'tryg', label: 'Tryg' },
        ],
        [],
    );

    const selectCountriesOptions = useMemo(
        () => [
            { code: 'SE', value: 'SE', label: 'SE' },
            { code: 'FI', value: 'FI', label: 'FI' },
            { code: 'DK', value: 'DK', label: 'DK' },
            { code: 'NO', value: 'NO', label: 'NO' },
            { code: 'IS', value: 'IS', label: 'IS' },
        ],
        [],
    );

    const [action, setAction] = useState('create');
    const [errors, setErrors] = useState({});
    const [isStateLoaded, setIsStateLoaded] = useState(false);

    const [data, setData] = useState({
        title: '',
        partner: '',
        vendor: '',
        market: [],
        localizations: {
            sv: {
                title: '',
                content: '',
                info_boxes: [],
            },
            no: {
                title: '',
                content: '',
                info_boxes: [],
            },
            fi: {
                title: '',
                content: '',
                info_boxes: [],
            },
            da: {
                title: '',
                content: '',
                info_boxes: [],
            },
            en: {
                title: '',
                content: '',
                info_boxes: [],
            },
            is: {
                title: '',
                content: '',
                info_boxes: [],
            },
        },
    });

    const fetchData = useCallback(() => {
        dispatch(getClients({ name: '', limit: 10000 })).unwrap();
    }, [dispatch]);

    useEffect(() => {
        fetchData();
        if (location?.pathname?.includes('edit')) {
            setAction('edit');
            dispatch(getContactById(params?.id));
        }
    }, [dispatch, fetchData, location, params]);

    useEffect(() => {
        const contactCountries =
            contact?.market &&
            selectCountriesOptions.filter((item) =>
                contact?.market?.includes(item.code),
            );

        setData({
            title: contact?.title || '',
            partner: contact?.partner || null,
            vendor: contact?.vendor || 'mysafety',
            market: contactCountries || selectCountriesOptions,
            localizations: {
                sv: {
                    content: countriesLocalizationsData?.sv?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.sv?.info_boxes || [],
                },
                no: {
                    content: countriesLocalizationsData?.no?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.no?.info_boxes || [],
                },
                fi: {
                    content: countriesLocalizationsData?.fi?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.fi?.info_boxes || [],
                },
                da: {
                    content: countriesLocalizationsData?.da?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.da?.info_boxes || [],
                },
                en: {
                    content: countriesLocalizationsData?.en?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.en?.info_boxes || [],
                },
                is: {
                    content: countriesLocalizationsData?.is?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.is?.info_boxes || [],
                },
            },
        });

        if (location?.pathname?.includes('create') || contact?.id) {
            setIsStateLoaded(true);
        }
    }, [
        countriesLocalizationsData,
        contact,
        location?.pathname,
        selectCountriesOptions,
    ]);

    useEffect(() => {
        return () => dispatch(resetContact());
    }, [dispatch]);

    const onInputChange = useCallback(
        (e) => {
            const { value } = e.target;
            const dataCopy = createShallowCopy(data);

            dataCopy.title = value;

            setData(dataCopy);
        },
        [data],
    );

    const onSelectChange = useCallback(
        (option, selectName) => {
            setData({ ...data, [selectName]: option.value });
        },
        [data],
    );

    const onMultiselectChange = useCallback(
        (options, selectName) => {
            setData({ ...data, [selectName]: options });
        },
        [data],
    );

    const onChangeMultinational = useCallback(
        (text, flag) => {
            const dataCopy = createShallowCopy(data);
            dataCopy.localizations[flag].content = text;

            setData(dataCopy);
        },
        [data],
    );

    const onAddInfoBox = useCallback(
        (flag) => {
            const dataCopy = createShallowCopy(data);

            dataCopy.localizations[flag].info_boxes.push({
                content: '',
                title: '',
            });

            setData(dataCopy);
        },
        [data],
    );

    const onChangeInfoBox = useCallback(
        ({ text, flag, id, field }) => {
            const dataCopy = createShallowCopy(data);
            const infoBoxIndex = dataCopy.localizations[
                flag
            ].info_boxes.findIndex((el) => el.id === id);
            const infoBox =
                dataCopy.localizations[flag].info_boxes[infoBoxIndex];

            infoBox[field] = text;

            setData(dataCopy);
        },
        [data],
    );

    const onRemoveInfoBox = useCallback(
        (flag, id) => {
            const dataCopy = createShallowCopy(data);

            const filteredInfoBoxes = dataCopy.localizations[
                flag
            ].info_boxes.filter((el) => el.id !== id);
            dataCopy.localizations[flag].info_boxes = filteredInfoBoxes;

            setData(dataCopy);
        },
        [data],
    );

    const onCancelButtonClick = useCallback(
        (e) => {
            e.preventDefault();
            navigate(-1);
        },
        [navigate],
    );

    const createRequestLocalizationsData = useCallback(
        (data) =>
            Object.entries(data.localizations).map((el) => ({
                lang_code: el[0].toUpperCase(),
                ...el[1],
            })),
        [],
    );

    const onCreatePage = useCallback(
        async (e) => {
            e.preventDefault();

            const requestLocalizationsData =
                createRequestLocalizationsData(data);
            const request = {
                ...data,
                localizations: requestLocalizationsData,
                market: data.market.map((el) => el.code),
            };

            try {
                await dispatch(createContact(request)).unwrap();
                toast.success('New page successfully created');
                navigate('../contact');
            } catch (error) {
                setErrors(error.errors);
            }
        },
        [createRequestLocalizationsData, data, dispatch, navigate],
    );

    const onUpdatePage = useCallback(
        async (e) => {
            e.preventDefault();

            const requestLocalizationsData =
                createRequestLocalizationsData(data);
            const request = {
                ...data,
                localizations: requestLocalizationsData,
                market: data.market.map((el) => el.code),
            };

            try {
                await dispatch(
                    editContact({ body: request, id: params.id }),
                ).unwrap();
                toast.success('Changes saved successfully');
                navigate('../contact');
            } catch (error) {
                setErrors(error.errors);
            }
        },
        [createRequestLocalizationsData, data, dispatch, navigate, params.id],
    );

    return (
        <div>
            <Title subText="Please fill information below">
                {action === 'create' ? 'Add new page' : 'Edit page'}
            </Title>
            <form>
                <Row>
                    <Col span={10}>
                        <div className="form__item-wrapper">
                            <CustomInput
                                id="title"
                                placeholder="Enter page title"
                                onInputChange={onInputChange}
                                inputValue={data?.title}
                                labelText="Page title"
                                width="100%"
                                errorText={errors?.title}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Partner</p>
                            <CustomSelect
                                name="partner"
                                onChange={(option) =>
                                    onSelectChange(option, 'partner')
                                }
                                options={selectPartnersOptions}
                                placeholder="Select partner"
                                value={data?.partner}
                                defaultValue={null}
                                errorText={errors?.partner}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Provider</p>
                            <CustomSelect
                                name="vendor"
                                onChange={(option) =>
                                    onSelectChange(option, 'vendor')
                                }
                                options={selectProvidersOptions}
                                placeholder="Select provider"
                                value={data?.vendor}
                                defaultValue={null}
                                errorText={errors?.vendor}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Market</p>
                            <CustomMultiselect
                                name="market"
                                onChange={(option) =>
                                    onMultiselectChange(option, 'market')
                                }
                                options={selectCountriesOptions}
                                placeholder="Select countries"
                                value={data?.market}
                                errorText={errors?.market}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <Divider />
                    </Col>
                </Row>
                <Row>
                    <Col span={20}>
                        <LocalLoader isLoading={!isStateLoaded}>
                            <MultinationalTabs>
                                <ContactEditors
                                    data={data?.localizations}
                                    onChange={onChangeMultinational}
                                    onAddInfoBox={onAddInfoBox}
                                    onRemoveInfoBox={onRemoveInfoBox}
                                    onChangeInfoBox={onChangeInfoBox}
                                    errors={errors}
                                />
                            </MultinationalTabs>
                        </LocalLoader>
                    </Col>
                </Row>
                <Row>
                    <Col span={10}>
                        <div className="form__footer">
                            <button
                                className="modal__button modal__button-cancel"
                                onClick={onCancelButtonClick}
                            >
                                Cancel
                            </button>
                            <button
                                className="modal__button modal__button-accept"
                                onClick={
                                    action === 'edit'
                                        ? onUpdatePage
                                        : onCreatePage
                                }
                            >
                                {action === 'edit' ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </form>
        </div>
    );
}
